import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, X, ClipboardList, Loader2, Play, Clock, BookOpen } from 'lucide-react';

export default function TestsPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [questionStats, setQuestionStats] = useState({});

  const [form, setForm] = useState({
    title: '',
    description: '',
    duration: 180,
    mathCount: 80,
    physicsCount: 40,
    chemCount: 40,
    correctMarks: 1,
    incorrectMarks: 0
  });

  useEffect(() => {
    fetchTests();
    fetchQuestionStats();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tests');
      setTests(data.data);
    } catch {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionStats = async () => {
    try {
      const { data } = await api.get('/questions/stats');
      const bySubject = {};
      data.data.bySubject.forEach(s => bySubject[s._id] = s.count);
      setQuestionStats(bySubject);
    } catch { }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error('Title is required');

    const total = form.mathCount + form.physicsCount + form.chemCount;
    if (total === 0) return toast.error('Total questions must be > 0');

    // Check availability
    if (form.mathCount > (questionStats.Mathematics || 0)) {
      return toast.error(`Not enough Mathematics questions. Available: ${questionStats.Mathematics || 0}`);
    }
    if (form.physicsCount > (questionStats.Physics || 0)) {
      return toast.error(`Not enough Physics questions. Available: ${questionStats.Physics || 0}`);
    }
    if (form.chemCount > (questionStats.Chemistry || 0)) {
      return toast.error(`Not enough Chemistry questions. Available: ${questionStats.Chemistry || 0}`);
    }

    setFormLoading(true);
    try {
      await api.post('/tests', {
        title: form.title,
        description: form.description,
        duration: form.duration,
        subjectDistribution: {
          Mathematics: parseInt(form.mathCount),
          Physics: parseInt(form.physicsCount),
          Chemistry: parseInt(form.chemCount)
        },
        markingScheme: {
          correct: parseFloat(form.correctMarks),
          incorrect: parseFloat(form.incorrectMarks),
          unattempted: 0
        }
      });
      toast.success('Test created successfully! Questions selected randomly.');
      setShowModal(false);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create test');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete test "${title}"?`)) return;
    try {
      await api.delete(`/tests/${id}`);
      toast.success('Test deleted');
      fetchTests();
    } catch {
      toast.error('Failed to delete test');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mock Tests</h1>
          <p className="text-gray-500 mt-1">Create and manage examination papers</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Create Test
        </button>
      </div>

      {/* Question availability */}
      <div className="card bg-blue-50 border-blue-100">
        <p className="font-semibold text-blue-900 mb-3">📊 Question Bank Availability</p>
        <div className="grid grid-cols-3 gap-4">
          {['Mathematics', 'Physics', 'Chemistry'].map(s => (
            <div key={s} className="text-center">
              <p className="text-2xl font-bold text-blue-700">{questionStats[s] || 0}</p>
              <p className="text-sm text-blue-600">{s}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tests list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="loader"></div></div>
      ) : tests.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          <ClipboardList size={48} className="mx-auto mb-2 opacity-30" />
          <p className="text-lg font-medium">No tests created yet</p>
          <p className="text-sm mt-1">Create your first mock test</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tests.map(test => (
            <div key={test._id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-gray-900">{test.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${test.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {test.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {test.description && <p className="text-sm text-gray-500 mb-3">{test.description}</p>}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <BookOpen size={14} className="text-primary-500" />
                      {test.totalQuestions} Questions
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} className="text-primary-500" />
                      {test.duration} minutes
                    </span>
                    <span className="text-blue-600">Math: {test.subjectDistribution?.Mathematics || 0}</span>
                    <span className="text-green-600">Physics: {test.subjectDistribution?.Physics || 0}</span>
                    <span className="text-amber-600">Chem: {test.subjectDistribution?.Chemistry || 0}</span>
                  </div>
                  <div className="flex gap-3 mt-3 text-xs text-gray-400">
                    <span>✅ +{test.markingScheme?.correct} for correct</span>
                    <span>❌ {test.markingScheme?.incorrect} for incorrect</span>
                    <span>Created: {new Date(test.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(test._id, test.title)}
                  className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors flex-shrink-0">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Test Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4 animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Create New Mock Test</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Title *</label>
                <input type="text" className="input-field" placeholder="e.g. EAMCET Mock Test 2024 - Set 1"
                  value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea className="input-field" rows={2} placeholder="Brief description of this test..."
                  value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <input type="number" className="input-field" min={5} max={300}
                  value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) })} />
              </div>

              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="font-semibold text-gray-700 text-sm">Question Distribution</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-blue-700 mb-1">Mathematics <span className="text-gray-400">(max {questionStats.Mathematics || 0})</span></label>
                    <input type="number" className="input-field" min={0} max={questionStats.Mathematics || 999}
                      value={form.mathCount} onChange={e => setForm({ ...form, mathCount: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-green-700 mb-1">Physics <span className="text-gray-400">(max {questionStats.Physics || 0})</span></label>
                    <input type="number" className="input-field" min={0} max={questionStats.Physics || 999}
                      value={form.physicsCount} onChange={e => setForm({ ...form, physicsCount: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-amber-700 mb-1">Chemistry <span className="text-gray-400">(max {questionStats.Chemistry || 0})</span></label>
                    <input type="number" className="input-field" min={0} max={questionStats.Chemistry || 999}
                      value={form.chemCount} onChange={e => setForm({ ...form, chemCount: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="text-center text-sm text-gray-600 font-medium">
                  Total: <span className="text-primary-700 font-bold">{form.mathCount + form.physicsCount + form.chemCount}</span> questions
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-xl p-4">
                <div>
                  <label className="block text-xs font-medium text-green-700 mb-1">Marks for Correct</label>
                  <input type="number" className="input-field" step={0.25}
                    value={form.correctMarks} onChange={e => setForm({ ...form, correctMarks: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-red-700 mb-1">Marks for Incorrect</label>
                  <input type="number" className="input-field" step={0.25}
                    value={form.incorrectMarks} onChange={e => setForm({ ...form, incorrectMarks: parseFloat(e.target.value) })} />
                </div>
              </div>

              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                💡 Questions will be automatically selected randomly from the question bank based on subject distribution.
              </p>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={formLoading} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  {formLoading ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
                  {formLoading ? 'Creating...' : 'Create Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
